<template>
	<InstructionPopoverButton
		v-if="loaded && !visibleCollections.length"
		:label="$t('Grade-level goals')"
		:instruction="$t('No grade-level goals have been set yet for your classroom(s) or group(s).')"
		:CTAText="$t('Learn more about getting started with grade-level goals')"
		@clickCTA="$emit('clickLearnMore')"
	/>

	<div v-else class="relative inline-block">
		<button type="button" class="btn btn-sm btn-outline" :disabled="!loaded" @click="openMenu">
			<span v-if="!loaded" class="loading loading-spinner loading-sm"></span>
			{{ $t('Grade-level goals') }}
			<ChevronDownIcon class="w-4 h-4 ml-1" />
		</button>

		<Dropdown ref="menu" position="bottom-start" class="w-96" teleport>
			<ul class="menu bg-base rounded-box shadow-lg border border-base-200 max-h-96 overflow-y-auto flex flex-col divide-y flex-nowrap">
				<li v-for="entry in entries" :key="entry.id">
					<a @click.prevent="handleEntryClick(entry)">
						{{ entry.label }}
					</a>
				</li>
			</ul>
		</Dropdown>
	</div>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import Dropdown from "@/components/ui/Dropdown.v2.vue";
import InstructionPopoverButton from "@/components/ui/InstructionPopoverButton.vue";
import { GoalCollection } from "@/models/GoalCollection.model";
import { Curriculum } from "@/models/Curriculum.model";
import { useGoalCollectionsStore } from "@/stores/GoalCollections.store";
import { useCurriculaStore } from "@/stores/Curricula.store";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { ChevronDownIcon } from "@heroicons/vue/24/solid";
import { useClassroomsStore } from "@/stores/Classrooms.store";

interface CollectionEntry {
	id: string;
	collection: GoalCollection;
	notYetCovered: boolean;
	label: string;
}

export default {
	emits: ['openCollection'],

	components: {
		Dropdown,
		InstructionPopoverButton,
		ChevronDownIcon,
	},

	props: {
		// 'school' additionally restricts the list to collections assigned to the
		// current classroom (agenda flows); 'global' offers every collection.
		scope: {
			type: String,
			required: true,
			validator: (value: string) => ['global', 'school'].includes(value),
		},
	},

	data() {
		return {
			loaded: false,
		};
	},

	computed: {
		...mapStores(useGoalCollectionsStore, useCurriculaStore, useOrganisationsStore, useClassroomsStore),

		availableKeys(): Set<string> {
			return new Set(this.curriculaStore.availableForUser.map((c: Curriculum) => c.key ?? c.id.toLowerCase()));
		},

		schoolCurriculum(): Curriculum | null {
			return this.organisationsStore.currentOrganisation?.curriculum ?? null;
		},

		visibleCollections(): GoalCollection[] {
			// One collection = one curriculum: collections whose curriculum the user
			// cannot offer goals from are hidden outright (spec 2026-07-29). Legacy
			// NULL-curriculum collections resolve to the school's curriculum and stay.
			return this.goalCollectionsStore.items.filter((c: GoalCollection) => {
				if (!c.editable) {
					return false;
				}
				if (this.scope === 'school'
					&& (this.currentClassroomId === null || !c.classroomIds.includes(this.currentClassroomId))) {
					return false;
				}
				const key = this.collectionKey(c);
				return key !== null && this.availableKeys.has(key);
			});
		},

		currentClassroomId(): string | null {
			return this.classroomsStore.currentClassroom?.id ?? null;
		},

		/**
		 * Matching the selected school's curriculum first. ("Newest school year
		 * first" needs a schoolYear field on the API — follow-up; store order kept
		 * within groups until then.)
		 */
		sortedCollections(): GoalCollection[] {
			const schoolKey = this.schoolCurriculum ? (this.schoolCurriculum.key ?? this.schoolCurriculum.id.toLowerCase()) : null;
			return [...this.visibleCollections].sort((a, b) => {
				const aMatch = this.collectionKey(a) === schoolKey ? 0 : 1;
				const bMatch = this.collectionKey(b) === schoolKey ? 0 : 1;
				return aMatch - bMatch;
			});
		},

		entries(): CollectionEntry[] {
			const multiCurriculum = this.curriculaStore.availableForUser.length > 1;
			const result: CollectionEntry[] = [];

			for (const collection of this.sortedCollections) {
				const curriculum = this.collectionCurriculum(collection);
				const suffix = multiCurriculum && curriculum ? ` (${curriculum.name})` : '';

				result.push({
					id: `${collection.id}:all`,
					collection,
					notYetCovered: false,
					label: this.$t('Goals in {collection}', { collection: `"${collection.name}"` }) + suffix,
				});
				result.push({
					id: `${collection.id}:uncovered`,
					collection,
					notYetCovered: true,
					label: this.$t('Goals in {collection} not yet scheduled this school year', { collection: `"${collection.name}"` }) + suffix,
				});
			}

			return result;
		},
	},

	async mounted() {
		await this.goalCollectionsStore.loadOnce();
		this.loaded = true;
	},

	methods: {
		collectionCurriculum(collection: GoalCollection): Curriculum | null {
			// Legacy collections (pre-backfill) have no curriculum yet: they can only
			// ever hold school-curriculum goals, so fall back to the school's.
			return collection.curriculum ?? this.schoolCurriculum;
		},

		collectionKey(collection: GoalCollection): string | null {
			const curriculum = this.collectionCurriculum(collection);
			return curriculum ? (curriculum.key ?? curriculum.id.toLowerCase()) : null;
		},

		openMenu() {
			this.$refs.menu.open();
		},

		handleEntryClick(entry: CollectionEntry) {
			this.$refs.menu.close();
			this.$emit('openCollection', { collection: entry.collection, notYetCovered: entry.notYetCovered });
		},
	},
};
</script>
